import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { SharedModalComponent } from '@kanek/shared-ui';

/**
 * Factory that produces a `CanDeactivateFn` showing a confirmation dialog
 * when the component has unsaved changes.
 *
 * @param isDirtyFn  Returns `true` when the component has unsaved changes.
 * @param message    Dialog body text.
 * @param onConfirm  Optional side-effect run on the component just before
 *                   navigation is allowed (e.g. resetting a writable dirty flag).
 */
export function createUnsavedChangesGuard<T>(
  isDirtyFn: (component: T) => boolean,
  message = 'You have unsaved changes. Are you sure you want to leave? All unsaved changes will be lost.',
  onConfirm?: (component: T) => void
): CanDeactivateFn<T> {
  return (component: T): Observable<boolean> | boolean => {
    if (!isDirtyFn(component)) return true;

    const dialog = inject(MatDialog);

    const dialogRef = dialog.open(SharedModalComponent, {
      data: {
        config: SharedModalComponent.createConfirmationConfig('Unsaved Changes', message, {
          icon: 'warning',
          iconColor: 'var(--sys-warning, #ff9800)',
          width: '400px',
        }),
      },
      disableClose: true,
    });

    return new Observable<boolean>(observer => {
      let resolved = false;

      const actionSub = dialogRef.componentInstance.actionEmitted.subscribe(
        (action: string) => {
          if (action === 'confirm') {
            resolved = true;
            onConfirm?.(component);
            observer.next(true);
            observer.complete();
            dialogRef.close();
          } else if (action === 'cancel') {
            resolved = true;
            observer.next(false);
            observer.complete();
            dialogRef.close();
          }
        }
      );

      // When the X button closes the dialog it bypasses actionEmitted entirely.
      // afterClosed() catches that path and treats it the same as "Cancel".
      const closedSub = dialogRef.afterClosed().subscribe(() => {
        if (!resolved) {
          observer.next(false);
          observer.complete();
        }
      });

      return () => {
        actionSub.unsubscribe();
        closedSub.unsubscribe();
        if (dialogRef.getState() === 0) dialogRef.close();
      };
    });
  };
}

/**
 * Attaches a `beforeunload` listener that shows the browser's native
 * "Leave site?" prompt when the page is about to be unloaded with unsaved changes.
 *
 * Returns a cleanup function — call it in `ngOnDestroy`.
 *
 * @param isDirtyFn  Returns `true` when there are unsaved changes.
 */
export function setupBeforeUnloadGuard(isDirtyFn: () => boolean): () => void {
  const handler = (event: BeforeUnloadEvent) => {
    if (isDirtyFn()) {
      event.preventDefault();
      event.returnValue = '';
    }
  };
  window.addEventListener('beforeunload', handler);
  return () => window.removeEventListener('beforeunload', handler);
}
